"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Constants
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 400;
const RULER_SIZE = 30;
const BASE_MM_TO_PX = 3.7795275591;
const CORNER_RADIUS = 10;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 190;
const MAX_WIDTH = 1470;
const MAX_HEIGHT = 1200;

const RECOMMENDED_SIZES = [
  { width: 590, height: 300, name: "DS-113" },
  { width: 580, height: 190, name: "DS-125" },
  { width: 760, height: 300, name: "DS-200" },
  { width: 280, height: 280, name: "DS-280" },
  { width: 684, height: 454, name: "DS-548" },
  { width: 840, height: 549, name: "DS-577" },
  { width: 1200, height: 300, name: "DS-1200" },
  { width: 1000, height: 583, name: "RB1000" },
  { width: 1130, height: 636, name: "RB1130" },
  { width: 1470, height: 875, name: "RB1470" },
  { width: 300, height: 1200, name: "RBVERT" },
] as const;

interface SizeSelectorProps {
  maxWidth?: number;
  maxHeight?: number;
  minWidth?: number;
  minHeight?: number;
}

const SizeSelector: React.FC<SizeSelectorProps> = ({
  maxWidth = MAX_WIDTH,
  maxHeight = MAX_HEIGHT,
  minWidth = MIN_WIDTH,
  minHeight = MIN_HEIGHT,
}) => {
  const defaultSize = RECOMMENDED_SIZES.find((size) => size.name === "DS-577")!;

  const [width, setWidth] = useState<number>(defaultSize.width);
  const [height, setHeight] = useState<number>(defaultSize.height);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [recommendedSize, setRecommendedSize] = useState<string | null>(null);

  const getScaleFactor = () => {
    const maxWidthPx = maxWidth * BASE_MM_TO_PX;
    const maxHeightPx = maxHeight * BASE_MM_TO_PX;
    const availableWidth = CANVAS_WIDTH - RULER_SIZE * 1.5;
    const availableHeight = CANVAS_HEIGHT - RULER_SIZE * 1.5;

    return Math.min(
      availableWidth / maxWidthPx,
      availableHeight / maxHeightPx,
      1
    );
  };

  const MM_TO_PX = BASE_MM_TO_PX * getScaleFactor();

  // Fix canvas sizing and DPI scaling
  const setupHiDPICanvas = (canvas: HTMLCanvasElement) => {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // Set the CSS size to match the container
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    ctx.scale(dpr, dpr);
    return ctx;
  };

  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  // Add this helper function after the constants
  const getClosestRecommendedSize = (width: number, height: number) => {
    return RECOMMENDED_SIZES.reduce((closest, current) => {
      const currentDiff =
        Math.abs(current.width - width) + Math.abs(current.height - height);
      const closestDiff =
        Math.abs(closest.width - width) + Math.abs(closest.height - height);
      return currentDiff < closestDiff ? current : closest;
    });
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const container = canvas.parentElement;
    if (!container) return;

    // Calculate desired dimensions while maintaining aspect ratio
    const containerWidth = container.clientWidth;
    const containerHeight = (containerWidth * CANVAS_HEIGHT) / CANVAS_WIDTH;

    // Update canvas style dimensions
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;

    const ctx = setupHiDPICanvas(canvas);
    if (!ctx) return;

    // Scale all drawing operations to match the new canvas size
    const scaleX = containerWidth / CANVAS_WIDTH;
    const scaleY = containerHeight / CANVAS_HEIGHT;
    ctx.scale(scaleX, scaleY);

    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw rulers background
    ctx.fillStyle = "#333333"; // Dark gray background
    ctx.fillRect(0, 0, CANVAS_WIDTH, RULER_SIZE);
    ctx.fillRect(0, 0, RULER_SIZE, CANVAS_HEIGHT);

    // Draw ruler marks
    ctx.strokeStyle = "white"; // White for ticks
    ctx.fillStyle = "white"; // White text for labels
    ctx.font = "10px Arial";
    ctx.lineWidth = 1;

    const rulerStep = 100; // Increment of 100mm

    // Horizontal ruler marks
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    for (let i = 0; i <= Math.max(maxWidth, 1500); i += rulerStep) {
      const x = i * MM_TO_PX + RULER_SIZE;
      if (x <= CANVAS_WIDTH) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, RULER_SIZE);
        ctx.stroke();
        ctx.fillText(i.toString(), x + 2, RULER_SIZE / 2);
      }
    }

    // Vertical ruler marks
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (let i = 0; i <= maxHeight; i += rulerStep) {
      const y = i * MM_TO_PX + RULER_SIZE;
      if (y <= CANVAS_HEIGHT) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(RULER_SIZE, y);
        ctx.stroke();
        ctx.fillText(i.toString(), RULER_SIZE / 2, y + 2);
      }
    }

    // Draw recommended size rectangle if different from current size
    const recommendedSize = getClosestRecommendedSize(width, height);
    if (recommendedSize.width !== width || recommendedSize.height !== height) {
      const recWidth = recommendedSize.width * MM_TO_PX;
      const recHeight = recommendedSize.height * MM_TO_PX;

      ctx.fillStyle = "rgba(156, 163, 175, 0.1)"; // Very faint gray
      ctx.strokeStyle = "rgba(156, 163, 175, 0.3)"; // Slightly more visible gray
      ctx.lineWidth = 1;

      drawRoundedRect(
        ctx,
        RULER_SIZE,
        RULER_SIZE,
        recWidth,
        recHeight,
        CORNER_RADIUS
      );

      ctx.fill();
      ctx.stroke();
    }

    // Draw rounded rectangle
    const rectWidth = width * MM_TO_PX;
    const rectHeight = height * MM_TO_PX;
    const cornerRadius = CORNER_RADIUS * MM_TO_PX;

    ctx.fillStyle = "rgba(59, 130, 246, 0.2)";
    ctx.strokeStyle = "#3b82f6";
    ctx.lineWidth = 2;

    drawRoundedRect(
      ctx,
      RULER_SIZE,
      RULER_SIZE,
      rectWidth,
      rectHeight,
      cornerRadius
    );

    ctx.fill();
    ctx.stroke();

    // Draw dimensions
    ctx.fillStyle = "#6B7280"; // Light gray color
    ctx.textAlign = "center";
    ctx.font = "12px Arial";
    ctx.fillText(
      `${width}mm`,
      RULER_SIZE + rectWidth / 2,
      RULER_SIZE + rectHeight + 20
    );

    // Move height text to right side
    ctx.textAlign = "left";
    ctx.fillText(
      `${height}mm`,
      RULER_SIZE + rectWidth + 10,
      RULER_SIZE + rectHeight / 2
    );
  };

  useEffect(() => {
    const closest = RECOMMENDED_SIZES.find((size) => {
      const widthDiff = Math.abs(size.width - width);
      const heightDiff = Math.abs(size.height - height);
      return widthDiff <= 5 && heightDiff <= 5;
    });

    setRecommendedSize(closest ? closest.name : null);
  }, [width, height]);

  useEffect(() => {
    drawCanvas();
    window.addEventListener("resize", drawCanvas);
    return () => window.removeEventListener("resize", drawCanvas);
  });

  return (
      <Card className='w-full'>
        <CardHeader>
          <CardTitle>Size Selector (mm)</CardTitle>
          <CardDescription className='text-2xl'>
            Recommended Size: {recommendedSize}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='space-y-2'>
              <label className='text-sm font-medium'>Width (mm)</label>
              <div className='flex space-x-4'>
                <Slider
                  value={[width]}
                  onValueChange={(value) => setWidth(value[0])}
                  min={minWidth}
                  max={maxWidth}
                  step={1}
                  className='flex-1'
                />
                <Input
                  type='number'
                  value={width}
                  onChange={(e) =>
                    setWidth(
                      Math.min(
                        maxWidth,
                        Math.max(minWidth, Number(e.target.value))
                      )
                    )
                  }
                  className='w-24'
                  min={minWidth}
                  max={maxWidth}
                />
              </div>
            </div>

            <div className='space-y-2'>
              <label className='text-sm font-medium'>Height (mm)</label>
              <div className='flex space-x-4'>
                <Slider
                  value={[height]}
                  onValueChange={(value) => setHeight(value[0])}
                  min={minHeight}
                  max={maxHeight}
                  step={1}
                  className='flex-1'
                />
                <Input
                  type='number'
                  value={height}
                  onChange={(e) =>
                    setHeight(
                      Math.min(
                        maxHeight,
                        Math.max(minHeight, Number(e.target.value))
                      )
                    )
                  }
                  className='w-24'
                  min={minHeight}
                  max={maxHeight}
                />
              </div>
            </div>
          </div>

          <div className='flex justify-between items-center'>
            <div className='flex flex-wrap gap-2'>
              {RECOMMENDED_SIZES.map((size) => (
                <Button
                  key={size.name}
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setWidth(size.width);
                    setHeight(size.height);
                  }}
                  className={
                    recommendedSize === size.name ? "border-blue-500" : ""
                  }
                >
                  {size.name}
                </Button>
              ))}
            </div>
          </div>

          <div className='border rounded-lg p-2 w-full'>
            <canvas ref={canvasRef} className='w-full h-auto' />
          </div>
        </CardContent>
      </Card>
  );
};

export default SizeSelector;